/**
 * Privacy-safe sharing for Beloved verses and prayers.
 *
 * Cards are rendered locally in the browser. No prayer text is uploaded in
 * order to make an image, and every path falls back to plain text when Canvas
 * or file sharing is unavailable.
 */

export const BELOVED_SHARE_URL = 'https://belovedapp.net/';

const CARD_WIDTH = 1080;
const CARD_HEIGHT = 1350;
const MAX_CARD_BODY_LENGTH = 950;

function clean(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

export function buildVerseShareText(verse) {
  const text = clean(verse && verse.text);
  const reference = clean(verse && verse.ref);
  return `“${text}”\n— ${reference}\n\nBeloved · Prayer when the words won’t come\n${BELOVED_SHARE_URL}`;
}

export function buildPrayerShareText(prayer) {
  const title = clean(prayer && prayer.title) || 'A prayer';
  const body = clean(prayer && (prayer.body || prayer.prayer));
  return `${title}\n\n${body}\n\nShared from Beloved\n${BELOVED_SHARE_URL}`;
}

export function isPersonalPrayer(prayer) {
  if (prayer && prayer.personal === true) return true;
  const id = clean(prayer && prayer.id).toLowerCase();
  const meta = clean(prayer && prayer.meta).toLowerCase();
  // `help-*` is the historic id used for generated personal prayers before a
  // dedicated `personal` flag was persisted. Treating it as personal keeps old
  // saved prayers behind the same explicit confirmation as new ones.
  return id.startsWith('personal-') || id.startsWith('help-') || meta.includes('personal prayer');
}

function wrapLines(context, text, maxWidth) {
  const words = clean(text).split(' ').filter(Boolean);
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (current && context.measureText(candidate).width > maxWidth) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  });
  if (current) lines.push(current);
  return lines;
}

function drawCentredLines(context, lines, startY, lineHeight) {
  lines.forEach((line, index) => {
    context.fillText(line, CARD_WIDTH / 2, startY + index * lineHeight);
  });
}

function canvasToFile(canvas) {
  try {
    // Keep this synchronous. Web Share requires a live user gesture; waiting
    // for canvas.toBlob() can cross a task boundary and make Safari reject the
    // otherwise valid share-sheet request.
    const dataUrl = canvas.toDataURL('image/png');
    const encoded = dataUrl.split(',')[1];
    if (!encoded || typeof atob !== 'function') return null;
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return new File([bytes], 'beloved-prayer.png', { type: 'image/png' });
  } catch (error) {
    return null;
  }
}

/**
 * Build the single restrained Beloved sharing card used for verses and short
 * prayers. Returns null rather than shrinking or truncating long content.
 */
export function createShareCard({ eyebrow, title, body, footer }) {
  if (
    typeof document === 'undefined' ||
    typeof File === 'undefined' ||
    clean(body).length > MAX_CARD_BODY_LENGTH
  ) {
    return null;
  }

  const canvas = document.createElement('canvas');
  if (!canvas || typeof canvas.getContext !== 'function') return null;
  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;
  const context = canvas.getContext('2d');
  if (!context) return null;

  const gradient = context.createLinearGradient(0, 0, CARD_WIDTH, CARD_HEIGHT);
  gradient.addColorStop(0, '#0D0D12');
  gradient.addColorStop(0.58, '#16161D');
  gradient.addColorStop(1, '#241D14');
  context.fillStyle = gradient;
  context.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  context.strokeStyle = 'rgba(212, 175, 55, 0.7)';
  context.lineWidth = 3;
  context.strokeRect(48, 48, CARD_WIDTH - 96, CARD_HEIGHT - 96);

  context.textAlign = 'center';
  context.textBaseline = 'top';
  context.fillStyle = '#D4AF37';
  context.font = '600 30px Georgia, serif';
  context.fillText(clean(eyebrow).toUpperCase(), CARD_WIDTH / 2, 120);

  context.fillStyle = '#E8C96B';
  context.font = '46px Georgia, serif';
  const titleLines = wrapLines(context, title, 820).slice(0, 3);
  drawCentredLines(context, titleLines, 190, 58);

  context.fillStyle = '#F8F5F0';
  context.font = '44px Georgia, serif';
  const bodyLines = wrapLines(context, body, 820);
  const bodyStart = 190 + titleLines.length * 58 + 78;
  const availableLines = Math.floor((1070 - bodyStart) / 61);
  if (bodyLines.length > availableLines) return null;
  drawCentredLines(context, bodyLines, bodyStart, 61);

  context.fillStyle = '#D8D2C6';
  context.font = '30px Georgia, serif';
  context.fillText(clean(footer), CARD_WIDTH / 2, 1110);
  context.fillStyle = '#D4AF37';
  context.font = '600 38px Georgia, serif';
  context.fillText('BELOVED', CARD_WIDTH / 2, 1190);
  context.fillStyle = '#D8D2C6';
  context.font = '26px Georgia, serif';
  context.fillText('belovedapp.net', CARD_WIDTH / 2, 1245);

  return canvasToFile(canvas);
}

async function copyText(text) {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    // Continue to the selection fallback below.
  }

  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch (error) {
    copied = false;
  }
  document.body.removeChild(textarea);
  return copied;
}

async function sharePayload({ title, text, card }) {
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  if (canShare) {
    try {
      if (
        card &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [card] })
      ) {
        await navigator.share({ title, text, files: [card] });
      } else {
        await navigator.share({ title, text, url: BELOVED_SHARE_URL });
      }
      return { status: 'shared' };
    } catch (error) {
      if (error && error.name === 'AbortError') {
        return { status: 'cancelled' };
      }
      // A failed share sheet should still offer a useful clipboard fallback.
    }
  }

  return (await copyText(text)) ? { status: 'copied' } : { status: 'unavailable' };
}

export async function shareVerse(verse) {
  const text = buildVerseShareText(verse);
  const card = createShareCard({
    eyebrow: "Today's verse",
    title: clean(verse && verse.ref),
    body: `“${clean(verse && verse.text)}”`,
    footer: 'Prayer when the words won’t come',
  });
  return sharePayload({ title: `Today’s verse · ${clean(verse && verse.ref)}`, text, card });
}

export async function sharePrayer(prayer, options = {}) {
  if (isPersonalPrayer(prayer) && options.confirmed !== true) {
    return { status: 'confirmation-required' };
  }
  const body = clean(prayer && (prayer.body || prayer.prayer));
  const title = clean(prayer && prayer.title) || 'A prayer';
  const text = buildPrayerShareText(prayer);
  const card = createShareCard({
    eyebrow: 'A prayer',
    title,
    body,
    footer: 'Shared from Beloved',
  });
  return sharePayload({ title: `${title} · Beloved`, text, card });
}
